import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../lib/api";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { inr, labelOf, PAYMENT_METHODS } from "../lib/constants";
import { Printer } from "lucide-react";

const Row = ({ label, value, testid }) => (
  <div className="flex justify-between py-2 border-b border-slate-100 text-sm">
    <span className="text-slate-500">{label}</span>
    <span className="font-medium text-ccc-navy text-right" data-testid={testid}>{value}</span>
  </div>
);

export default function Receipt() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get(`/patients/${id}`).then((r) => setP(r.data)).catch(() => setErr("Patient not found"));
  }, [id]);

  if (err) return <div className="p-10 text-slate-500" data-testid="receipt-error">{err}</div>;
  if (!p) return <div className="p-10 text-slate-400">Loading receipt…</div>;
  if (p.payment_status !== "paid") return <div className="p-10 text-slate-500" data-testid="receipt-not-paid">No receipt — this patient is not marked paid.</div>;

  const amount = (p.revenue_per_session || 0) * (p.sessions_total || 0);
  const receiptNo = `RCPT-${p.patient_code || p._id.slice(-6).toUpperCase()}-${(p.paid_date || "").replace(/-/g, "")}`;
  return (
    <div className="min-h-screen bg-slate-100 print:bg-white py-10 px-4">
      <div className="max-w-xl mx-auto mb-4 flex justify-end print:hidden">
        <Button onClick={() => window.print()} data-testid="receipt-print-button" className="bg-ccc-blue hover:bg-ccc-blue2 text-white"><Printer className="h-4 w-4 mr-2" />Print / Save PDF</Button>
      </div>
      <div className="max-w-xl mx-auto bg-white rounded-2xl border border-slate-200 print:border-0 p-8" data-testid="receipt-page">
        <div className="flex items-start justify-between border-b-4 border-ccc-blue pb-5">
          <Logo className="h-9" />
          <div className="text-right">
            <p className="text-xs uppercase tracking-widest text-slate-400">Payment receipt</p>
            <p className="font-mono text-sm font-semibold text-ccc-navy mt-1" data-testid="receipt-number">{receiptNo}</p>
          </div>
        </div>
        <div className="mt-6">
          <Row label="Patient" value={p.name} testid="receipt-patient-name" />
          <Row label="Patient ID" value={p.patient_code || "Not yet enrolled"} testid="receipt-patient-id" />
          <Row label="Paid on" value={p.paid_date || "—"} testid="receipt-paid-date" />
          <Row label="Paid via" value={p.payment_method ? labelOf(PAYMENT_METHODS, p.payment_method) : "—"} testid="receipt-method" />
          <Row label="Sessions" value={`${p.sessions_total || 0} × ${inr(p.revenue_per_session || 0)}`} testid="receipt-sessions" />
        </div>
        <div className="mt-6 bg-slate-50 rounded-xl p-5 flex justify-between items-center">
          <span className="text-sm font-semibold text-slate-600 uppercase tracking-wide">Amount paid</span>
          <span className="text-2xl font-bold font-head text-ccc-navy" data-testid="receipt-amount">{inr(amount)}</span>
        </div>
        <p className="text-xs text-slate-400 mt-8 text-center">Thank you. This is a computer-generated receipt from CorreCore.</p>
      </div>
    </div>
  );
}
